/**
 * The `sessionStorage` mirror (D8).
 *
 * **Why not `localStorage`.** The mistake this replaced stored the ID token in
 * `localStorage` and checks `exp` only on mount, so a tab left open past the hour keeps
 * sending a dead token and the player gets opaque failures — the actual auth bug
 * and the one D8 exists to fix. `sessionStorage` scopes the mirror to the tab that earned
 * it: it survives a reload (the case worth surviving), it does not survive the browser, and
 * it is never shared with another tab that might be signed in as someone else.
 *
 * The token still lives in memory. This is the mirror, not the source.
 */
import { decodeIdToken, isExpired, type IdToken } from '@/auth/token';

const STORAGE_KEY = 'quest.v2.id_token';

/** Returns null and clears the mirror if the stored token is unreadable or already dead. */
export function readSessionToken(now: number = Date.now()): IdToken | null {
  const credential = safeRead();
  if (credential === null) return null;

  const token = decodeIdToken(credential);
  if (token === null || isExpired(token, now)) {
    clearSessionToken();
    return null;
  }
  return token;
}

export function writeSessionToken(credential: string): void {
  safeWrite(credential);
}

export function clearSessionToken(): void {
  safeRemove();
}

// Storage throws rather than returning null in a few real configurations — Safari's private
// mode historically, and any browser with site data blocked. None of them should take the
// app down: a player with storage disabled simply signs in again after a reload.

function safeRead(): string | null {
  try {
    return globalThis.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function safeWrite(value: string): void {
  try {
    globalThis.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* no mirror; memory still holds the token for this page's lifetime */
  }
}

function safeRemove(): void {
  try {
    globalThis.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}
